import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, onSnapshot, setDoc } from 'firebase/firestore';
import { FaEdit, FaSave, FaTimes, FaParking, FaMapMarkerAlt, FaRoute, FaTrash, FaCar, FaMotorcycle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, FeatureGroup, Polyline } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Santo Domingo de los Colorados, Ecuador
const SANTO_DOMINGO_CENTER = [-0.2531, -79.1719];

function Zonificacion() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('zonificacion');
  const [tickets, setTickets] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState('Desconectado');
  const [, setLastUpdate] = useState(Date.now());
  const [zonaFilter, setZonaFilter] = useState(''); // Filtro de búsqueda por zona
  const [caFilter, setCaFilter] = useState(''); // Filtro de búsqueda por CA
  const [placaFilter, setPlacaFilter] = useState(''); // Filtro de búsqueda por placa

  // Estados para la configuración de zonas
  const [zoneConfigs, setZoneConfigs] = useState({});
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [editForm, setEditForm] = useState({ cantidadAutos: 0, cantidadMotos: 0, calles: '', linea: [] });

  const navigate = useNavigate();

  // Configurar escucha de tickets en tiempo real
  const setupRealtimeTickets = useCallback(() => {
    try {
      // Obtener la fecha actual en formato YYYY-MM-DD
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = (today.getMonth() + 1).toString().padStart(2, '0');
      const dd = today.getDate().toString().padStart(2, '0');
      const fechaActual = `${yyyy}-${mm}-${dd}`;

      // Referencia a la subcolección del día actual
      const ticketsRef = collection(db, 'ticketsVendidos', fechaActual, 'tickets');

      // Escuchar cambios en la subcolección en tiempo real
      const unsubscribe = onSnapshot(ticketsRef, (snapshot) => {
        setRealtimeStatus('Conectado');
        const ticketsList = [];

        snapshot.forEach((doc) => {
          const data = doc.data();

          ticketsList.push({
            id: doc.id,
            ...data,
            ticketPeriod: data.ticketPeriod || null,
            zona: data.zone || data.zona || 'Sin zona'
          });
        });

        setTickets([...ticketsList]);
        setLastUpdate(Date.now());
      }, (error) => {
        console.error("🔥 Zonificación REALTIME ERROR:", error);
        setRealtimeStatus('Error');
      });
      return unsubscribe;
    } catch (error) {
      console.error("Error al configurar listener de tickets:", error);
      return () => { };
    }
  }, []);

  useEffect(() => {
    if (user) {
      console.log("Configurando escucha de tickets en tiempo real...");
      const unsubscribe = setupRealtimeTickets();
      return () => unsubscribe();
    }
  }, [user, setupRealtimeTickets]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verificar si el perfil del usuario existe en Firestore
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);

          if (!userDoc.exists()) {
            console.error("Perfil de usuario no encontrado en la base de datos");
            await auth.signOut();
            navigate('/login');
            return;
          }

          // El usuario existe, continuar normalmente
          setUser(currentUser);

          // Almacenar el rol del usuario
          const userData = userDoc.data();
          setUserRole(userData.rol || 'Usuario');
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          await auth.signOut();
          navigate('/login');
          return;
        }
      } else {
        navigate('/login');
        return;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Escuchar configuración de zonas en tiempo real
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'zonificacion'), (snapshot) => {
      const configs = {};
      snapshot.forEach((doc) => {
        configs[doc.id] = doc.data();
      });
      setZoneConfigs(configs);
    }, (error) => {
      console.error("Error al cargar configuraciones de zona:", error);
    });
    return () => unsubscribe();
  }, []);

  // Manejar apertura del modal de edición
  const handleEditZone = (zona) => {
    if (!userRole || userRole.toLowerCase() !== 'admin') return;
    const currentConfig = zoneConfigs[zona] || {};

    setEditingZone(zona);
    // Si ya tiene cantidadAutos/Motos definidos, usar esos; si solo tiene `cantidad` legado, poner 0 para que admin los configure
    const defAutos = currentConfig.cantidadAutos != null ? currentConfig.cantidadAutos : 0;
    const defMotos = currentConfig.cantidadMotos != null ? currentConfig.cantidadMotos : 0;
    setEditForm({
      cantidadAutos: defAutos,
      cantidadMotos: defMotos,
      calles: currentConfig.calles || '',
      linea: currentConfig.linea || []
    });
    setIsConfigModalOpen(true);
  };

  // Manejar creación de línea en el mapa
  const handleLineCreated = (e) => {
    const layer = e.layer;
    // Para polylines, getLatLngs() devuelve directamente el array
    const latlngs = layer.getLatLngs().map(latlng => [latlng.lat, latlng.lng]);
    setEditForm(prev => ({ ...prev, linea: latlngs }));
  };

  // Manejar edición de línea
  const handleLineEdited = (e) => {
    const layers = e.layers;
    layers.eachLayer((layer) => {
      const latlngs = layer.getLatLngs().map(latlng => [latlng.lat, latlng.lng]);
      setEditForm(prev => ({ ...prev, linea: latlngs }));
    });
  };

  // Manejar eliminación de línea
  const handleLineDeleted = () => {
    setEditForm(prev => ({ ...prev, linea: [] }));
  };

  // Guardar configuración de zona
  const handleSaveConfig = async () => {
    if (!editingZone) return;

    try {
      const cantAutos = Number(editForm.cantidadAutos) || 0;
      const cantMotos = Number(editForm.cantidadMotos) || 0;
      const zoneData = {
        cantidadAutos: cantAutos,
        cantidadMotos: cantMotos,
        cantidad: cantAutos + cantMotos,
        calles: editForm.calles,
        linea: editForm.linea,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email
      };

      await setDoc(doc(db, 'zonificacion', editingZone), zoneData, { merge: true });

      // Cerrar modal y resetear
      setIsConfigModalOpen(false);
      setEditingZone(null);
    } catch (error) {
      console.error("Error al guardar configuración de zona:", error);
      alert("Error al guardar la configuración");
    }
  };

  // Función para verificar si un ticket está activo según ticketPeriod
  const isTicketActive = (ticket) => {
    if (!ticket || !ticket.ticketPeriod) return false;

    try {
      // Parsear ticketPeriod: "15:02 a 16:02" o "22:47 a 00:47" (cruza medianoche)
      const periodParts = ticket.ticketPeriod.split(' a ');
      if (periodParts.length !== 2) return false;

      const [startTime, endTime] = periodParts.map(s => s.trim());
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      // Obtener hora actual
      const now = new Date();
      const currentHour = now.getHours();
      const currentMin = now.getMinutes();

      // Convertir a minutos desde medianoche (00:00 = 0)
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      const currentMinutes = currentHour * 60 + currentMin;

      // Si endMinutes < startMinutes, significa que cruza medianoche
      // Ejemplo: 22:47 a 00:47 (22*60+47=1367 a 0*60+47=47)
      let isActive = false;

      if (endMinutes < startMinutes) {
        // Cruza medianoche: el ticket está activo si currentMinutes >= startMinutes O currentMinutes <= endMinutes
        isActive = currentMinutes >= startMinutes || currentMinutes <= endMinutes;
      } else {
        // No cruza medianoche: verificar si está dentro del rango normal
        isActive = currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }

      return isActive;
    } catch (error) {
      console.warn('Error al verificar ticketPeriod:', ticket.ticketPeriod, error);
      return false;
    }
  };

  // Filtrar solo tickets activos y agrupar por zona
  const ticketsByZone = tickets
    .filter(ticket => isTicketActive(ticket))
    .reduce((acc, ticket) => {
      const zona = ticket.zona || 'Sin zona';
      if (!acc[zona]) {
        acc[zona] = [];
      }
      acc[zona].push(ticket);
      return acc;
    }, {});

  // Combinar zonas con tickets y zonas configuradas para mostrar todas
  const allZonesSet = new Set([...Object.keys(ticketsByZone), ...Object.keys(zoneConfigs)]);
  const allZones = Array.from(allZonesSet).sort();

  const sortedZones = allZones.filter(zona => {
    // 1. Filtro por nombre de zona
    const matchesZone = zona.toLowerCase().includes(zonaFilter.toLowerCase());

    // 2. Filtro por CA (busca en configuración de zona O en los tickets de la zona)
    let matchesCa = true;
    if (caFilter) {
      const config = zoneConfigs[zona] || {};
      const zoneTickets = ticketsByZone[zona] || [];

      const configCaMatches = config.ca && config.ca.toLowerCase().includes(caFilter.toLowerCase());
      // Buscar en tickets: check variable 'numberCa' (del JSON usuario) o 'seller' o 'vendor'
      const ticketCaMatches = zoneTickets.some(t =>
        (t.numberCa && t.numberCa.toLowerCase().includes(caFilter.toLowerCase())) ||
        (t.ca && t.ca.toLowerCase().includes(caFilter.toLowerCase()))
      );

      matchesCa = configCaMatches || ticketCaMatches;
    }

    // 3. Filtro por placa (busca en tickets activos de la zona)
    let matchesPlaca = true;
    if (placaFilter) {
      const zoneTickets = ticketsByZone[zona] || [];
      matchesPlaca = zoneTickets.some(t =>
        t.plate && t.plate.toLowerCase().includes(placaFilter.toLowerCase())
      );
    }

    return matchesZone && matchesCa && matchesPlaca;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100 transition-all duration-300 ease-in-out">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 transition-all duration-300 ease-in-out">
      {/* Mobile Header */}
      <MobileHeader
        title="Zonificación"
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto transition-all duration-300 ease-in-out">
          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            {/* Primera fila: Título, fecha y usuario */}
            <div className="flex justify-between items-center mb-3">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Zonificación</h1>
                <p className="text-sm text-gray-600">
                  📅 Tickets de hoy - {new Date().toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${realtimeStatus === 'Conectado' ? 'bg-green-100 text-green-800' :
                    realtimeStatus === 'Error' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                    🔄 {realtimeStatus}
                  </span>
                </p>
              </div>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>

            {/* Segunda fila: Leyenda */}
            <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-200 pb-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border-2 border-gray-300 rounded mr-2"></div>
                <span className="text-sm text-gray-600">Espacio Libre</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2 flex items-center justify-center">
                  <FaCar size={12} className="text-white" />
                </div>
                <span className="text-sm text-gray-600">Auto Ocupado</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-orange-500 rounded mr-2 flex items-center justify-center">
                  <FaMotorcycle size={12} className="text-white" />
                </div>
                <span className="text-sm text-gray-600">Moto Ocupada</span>
              </div>
            </div>

            {/* Tercera fila: Buscador de zona */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex flex-col md:flex-row gap-4 w-full max-w-4xl">
                {/* Buscador de Zona */}
                <div className="flex-1">
                  <label htmlFor="zona-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar por Zona
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="zona-filter"
                      value={zonaFilter}
                      onChange={(e) => setZonaFilter(e.target.value)}
                      placeholder="Ej: Z 20-S100"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    {zonaFilter && (
                      <button
                        onClick={() => setZonaFilter('')}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                </div>

                {/* Buscador de CA */}
                <div className="flex-1">
                  <label htmlFor="ca-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar por CA
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="ca-filter"
                      value={caFilter}
                      onChange={(e) => setCaFilter(e.target.value)}
                      placeholder="Ej: CA194"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    {caFilter && (
                      <button
                        onClick={() => setCaFilter('')}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                </div>

                {/* Buscador de Placa */}
                <div className="flex-1">
                  <label htmlFor="placa-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar por Placa
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="placa-filter"
                      value={placaFilter}
                      onChange={(e) => setPlacaFilter(e.target.value.toUpperCase())}
                      placeholder="Ej: ABC-123"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase"
                    />
                    {placaFilter && (
                      <button
                        onClick={() => setPlacaFilter('')}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">
            {/* Mobile Header */}
            <div className="md:hidden bg-white shadow mb-4">
              {/* Primera fila: Título y fecha */}
              <div className="p-4 border-b border-gray-200">
                <h1 className="text-lg font-bold text-gray-900 mb-2">Zonificación</h1>
                <p className="text-xs text-gray-600">
                  📅 {new Date().toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${realtimeStatus === 'Conectado' ? 'bg-green-100 text-green-800' :
                    realtimeStatus === 'Error' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                    🔄 {realtimeStatus}
                  </span>
                </p>
              </div>

              {/* Segunda fila: Leyenda */}
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-white border-2 border-gray-300 rounded mr-2"></div>
                    <span className="text-xs text-gray-600">Libre</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-blue-500 rounded mr-2 flex items-center justify-center">
                      <FaCar size={10} className="text-white" />
                    </div>
                    <span className="text-xs text-gray-600">Auto</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-orange-500 rounded mr-2 flex items-center justify-center">
                      <FaMotorcycle size={10} className="text-white" />
                    </div>
                    <span className="text-xs text-gray-600">Moto</span>
                  </div>
                </div>
              </div>

              {/* Tercera fila: Buscador de zona */}
              <div className="p-4 space-y-3">
                {/* Zona Mobile */}
                <div>
                  <label htmlFor="zona-filter-mobile" className="block text-xs font-medium text-gray-700 mb-1">
                    Buscar por Zona
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="zona-filter-mobile"
                      value={zonaFilter}
                      onChange={(e) => setZonaFilter(e.target.value)}
                      placeholder="Ej: Z 20-S100"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    {zonaFilter && (
                      <button
                        onClick={() => setZonaFilter('')}
                        className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>

                {/* CA Mobile */}
                <div>
                  <label htmlFor="ca-filter-mobile" className="block text-xs font-medium text-gray-700 mb-1">
                    Buscar por CA
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="ca-filter-mobile"
                      value={caFilter}
                      onChange={(e) => setCaFilter(e.target.value)}
                      placeholder="Ej: CA194"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    {caFilter && (
                      <button
                        onClick={() => setCaFilter('')}
                        className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>

                {/* Placa Mobile */}
                <div>
                  <label htmlFor="placa-filter-mobile" className="block text-xs font-medium text-gray-700 mb-1">
                    Buscar por Placa
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="placa-filter-mobile"
                      value={placaFilter}
                      onChange={(e) => setPlacaFilter(e.target.value.toUpperCase())}
                      placeholder="Ej: ABC-123"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase"
                    />
                    {placaFilter && (
                      <button
                        onClick={() => setPlacaFilter('')}
                        className="px-3 py-2 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Zonas Grid */}
            {sortedZones.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500 text-lg">
                  {zonaFilter || caFilter || placaFilter
                    ? `No se encontraron zonas que coincidan con los filtros.`
                    : 'No hay zonas activas ni configuradas.'}
                </p>
                {(zonaFilter || caFilter || placaFilter) && (
                  <button
                    onClick={() => { setZonaFilter(''); setCaFilter(''); setPlacaFilter(''); }}
                    className="mt-4 text-blue-600 hover:text-blue-800 underline"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {sortedZones.map((zona) => {
                  const zoneTickets = ticketsByZone[zona] || [];
                  const config = zoneConfigs[zona] || {};

                  // Capacidad configurada por tipo (0 si aún no está configurada)
                  const capConfigAutos = config.cantidadAutos != null ? Number(config.cantidadAutos) : 0;
                  const capConfigMotos = config.cantidadMotos != null ? Number(config.cantidadMotos) : 0;

                  // Separar tickets por tipo
                  const autoTickets = zoneTickets.filter(t => (t.vehicleType || '').match(/auto/i));
                  const motoTickets = zoneTickets.filter(t => (t.vehicleType || '').match(/moto/i));

                  // Capacidad de visualización = máximo entre lo configurado y lo ocupado real
                  const capacidadAutos = Math.max(capConfigAutos, autoTickets.length);
                  const capacidadMotos = Math.max(capConfigMotos, motoTickets.length);

                  const ocupadosAutos = autoTickets.length;
                  const ocupadosMotos = motoTickets.length;
                  const libresAutos = Math.max(0, capacidadAutos - ocupadosAutos);
                  const libresMotos = Math.max(0, capacidadMotos - ocupadosMotos);

                  // Arrays de espacios por tipo
                  const espaciosAutos = Array.from({ length: capacidadAutos }, (_, i) => autoTickets[i] || null);
                  const espaciosMotos = Array.from({ length: capacidadMotos }, (_, i) => motoTickets[i] || null);

                  return (
                    <div key={zona} className="bg-white rounded-lg shadow-lg p-3 relative group transition-all duration-300 hover:shadow-xl">
                      {/* Botón de configuración (visible solo para admin) */}
                      {userRole && userRole.toLowerCase() === 'admin' && (
                        <button
                          onClick={() => handleEditZone(zona)}
                          className="absolute top-2 right-2 p-1.5 bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 rounded-full transition-colors z-10"
                          title="Configurar Zona"
                        >
                          <FaEdit size={14} />
                        </button>
                      )}

                      {/* Header de la zona */}
                      <div className="mb-2 pb-1.5 border-b border-gray-200 pr-8">
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-bold text-gray-900 truncate" title={zona}>{zona}</h2>
                          {(zoneTickets[0]?.numberCa || config.ca) && (
                            <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                              {zoneTickets[0]?.numberCa || config.ca}
                            </span>
                          )}
                        </div>
                        {/* Resumen en una sola línea */}
                        <div className="mt-1 flex items-center justify-between text-[10px]">
                          <span className="flex items-center gap-1 text-blue-700 font-semibold">
                            <FaCar size={9} /> {ocupadosAutos}/{capacidadAutos}
                            <span className="text-green-700">({libresAutos}L)</span>
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className="flex items-center gap-1 text-orange-600 font-semibold">
                            <FaMotorcycle size={9} /> {ocupadosMotos}/{capacidadMotos}
                            <span className="text-green-700">({libresMotos}L)</span>
                          </span>
                        </div>
                      </div>

                      {/* Sección Autos */}
                      {capacidadAutos > 0 && (
                        <div className="mb-1.5">
                          <div className="text-[8px] font-bold text-blue-600 uppercase mb-0.5 flex items-center gap-1">
                            <FaCar size={8} /> Autos
                          </div>
                          <div
                            className="grid"
                            style={{ gap: 0, gridTemplateColumns: `repeat(${capacidadAutos <= 5 ? capacidadAutos : 5}, minmax(0, 1fr))` }}
                          >
                            {espaciosAutos.map((ticket, index) => {
                              const hasTicket = ticket !== null;
                              const cols = capacidadAutos <= 5 ? capacidadAutos : 5;
                              const isLastColumn = (index + 1) % cols === 0;
                              const isLastRow = Math.ceil((index + 1) / cols) === Math.ceil(capacidadAutos / cols);
                              const borderClass = hasTicket
                                ? `${isLastColumn ? '' : 'border-r border-blue-800'} ${isLastRow ? '' : 'border-b border-blue-800'}`
                                : `${isLastColumn ? '' : 'border-r border-gray-300'} ${isLastRow ? '' : 'border-b border-gray-300'}`;
                              return (
                                <div
                                  key={index}
                                  className={`${hasTicket ? 'bg-blue-500' : 'bg-white'} ${borderClass} flex items-center justify-center h-7 w-full overflow-hidden`}
                                  style={{ margin: 0, padding: 0 }}
                                  title={hasTicket ? (ticket.plate || ticket.vehicleType) : `Espacio ${index + 1}`}
                                >
                                  {hasTicket ? (
                                    <span className="text-[9px] font-bold text-white leading-none text-center w-full truncate">
                                      {ticket.plate || '?'}
                                    </span>
                                  ) : (
                                    <span className="text-[8px] font-bold text-gray-400">{index + 1}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Sección Motos */}
                      {capacidadMotos > 0 && (
                        <div className="mb-1.5">
                          <div className="text-[8px] font-bold text-orange-600 uppercase mb-0.5 flex items-center gap-1">
                            <FaMotorcycle size={8} /> Motos
                          </div>
                          <div
                            className="grid"
                            style={{ gap: 0, gridTemplateColumns: `repeat(${capacidadMotos <= 5 ? capacidadMotos : 5}, minmax(0, 1fr))` }}
                          >
                            {espaciosMotos.map((ticket, index) => {
                              const hasTicket = ticket !== null;
                              const cols = capacidadMotos <= 5 ? capacidadMotos : 5;
                              const isLastColumn = (index + 1) % cols === 0;
                              const isLastRow = Math.ceil((index + 1) / cols) === Math.ceil(capacidadMotos / cols);
                              const borderClass = hasTicket
                                ? `${isLastColumn ? '' : 'border-r border-orange-800'} ${isLastRow ? '' : 'border-b border-orange-800'}`
                                : `${isLastColumn ? '' : 'border-r border-gray-300'} ${isLastRow ? '' : 'border-b border-gray-300'}`;
                              return (
                                <div
                                  key={index}
                                  className={`${hasTicket ? 'bg-orange-500' : 'bg-white'} ${borderClass} flex items-center justify-center h-7 w-full overflow-hidden`}
                                  style={{ margin: 0, padding: 0 }}
                                  title={hasTicket ? (ticket.plate || ticket.vehicleType) : `Espacio ${index + 1}`}
                                >
                                  {hasTicket ? (
                                    <span className="text-[9px] font-bold text-white leading-none text-center w-full truncate">
                                      {ticket.plate || '?'}
                                    </span>
                                  ) : (
                                    <span className="text-[8px] font-bold text-gray-400">{index + 1}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Pie: barras de progreso + nombre de calle */}
                      <div className="mt-2 pt-1.5 border-t border-gray-200 space-y-1">
                        {capacidadAutos > 0 && (
                          <div className="w-full bg-gray-200 rounded-full h-1">
                            <div
                              className={`h-1 rounded-full ${ocupadosAutos === capacidadAutos ? 'bg-red-500' : 'bg-blue-500'}`}
                              style={{ width: `${(ocupadosAutos / capacidadAutos) * 100}%` }}
                            ></div>
                          </div>
                        )}
                        {capacidadMotos > 0 && (
                          <div className="w-full bg-gray-200 rounded-full h-1">
                            <div
                              className={`h-1 rounded-full ${ocupadosMotos === capacidadMotos ? 'bg-red-500' : 'bg-orange-500'}`}
                              style={{ width: `${(ocupadosMotos / capacidadMotos) * 100}%` }}
                            ></div>
                          </div>
                        )}
                        {config.calles && (
                          <p className="text-[9px] text-gray-500 truncate pt-0.5" title={config.calles}>
                            📍 {config.calles}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Modal de Configuración con Mapa */}
            {isConfigModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                  {/* Header */}
                  <div className="bg-blue-600 px-6 py-4 flex justify-between items-center flex-shrink-0">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <FaRoute /> Configurar Zona: {editingZone}
                    </h3>
                    <button
                      onClick={() => setIsConfigModalOpen(false)}
                      className="text-white hover:text-blue-200 transition-colors"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left Column - Form Fields */}
                      <div className="space-y-4">
                        {/* Capacidad por tipo */}
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1 flex items-center gap-1">
                              <FaCar size={13} /> Puestos para Autos
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaParking className="text-blue-400" />
                              </div>
                              <input
                                type="number"
                                min="0"
                                value={editForm.cantidadAutos}
                                onChange={(e) => setEditForm({ ...editForm, cantidadAutos: e.target.value })}
                                className="pl-10 block w-full border-blue-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 shadow-sm sm:text-sm border p-2.5"
                                placeholder="Ej: 10"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-orange-600 mb-1 flex items-center gap-1">
                              <FaMotorcycle size={13} /> Puestos para Motos
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaParking className="text-orange-400" />
                              </div>
                              <input
                                type="number"
                                min="0"
                                value={editForm.cantidadMotos}
                                onChange={(e) => setEditForm({ ...editForm, cantidadMotos: e.target.value })}
                                className="pl-10 block w-full border-orange-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 shadow-sm sm:text-sm border p-2.5"
                                placeholder="Ej: 5"
                              />
                            </div>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex justify-between items-center">
                            <span className="text-sm text-gray-600 flex items-center gap-1">
                              <FaParking className="text-gray-400" /> Total de Puestos:
                            </span>
                            <span className="text-sm font-bold text-gray-900">
                              {(Number(editForm.cantidadAutos) || 0) + (Number(editForm.cantidadMotos) || 0)}
                            </span>
                          </div>
                        </div>

                        {/* Nombre de Calles */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre de las Calles
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaMapMarkerAlt className="text-gray-400" />
                            </div>
                            <input
                              type="text"
                              value={editForm.calles}
                              onChange={(e) => setEditForm({ ...editForm, calles: e.target.value })}
                              className="pl-10 block w-full border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 shadow-sm sm:text-sm border p-2.5"
                              placeholder="Ej: Av. Quito y Calle Latacunga"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Ingrese las calles que delimitan esta zona.
                          </p>
                        </div>

                        {/* Info de la Línea */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                            <FaRoute /> Línea de la Calle
                          </h4>
                          <p className="text-xs text-blue-600 mt-1">
                            {editForm.linea && editForm.linea.length > 0
                              ? `✓ Línea definida con ${editForm.linea.length} puntos`
                              : '⚠ Sin línea. Use las herramientas del mapa para dibujar una.'}
                          </p>
                          {editForm.linea && editForm.linea.length > 0 && (
                            <button
                              onClick={() => setEditForm({ ...editForm, linea: [] })}
                              className="mt-2 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                            >
                              <FaTrash size={10} /> Eliminar línea
                            </button>
                          )}
                        </div>

                        {/* Instrucciones */}
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="text-sm font-semibold text-gray-700">📍 Instrucciones:</h4>
                          <ul className="text-xs text-gray-600 mt-1 space-y-1">
                            <li>1. Use el icono de línea (polyline) para dibujar</li>
                            <li>2. Haga clic en el inicio de la calle</li>
                            <li>3. Agregue puntos a lo largo de la calle</li>
                            <li>4. Doble clic para terminar la línea</li>
                            <li>5. Use editar/eliminar para modificar</li>
                          </ul>
                        </div>
                      </div>

                      {/* Right Column - Map */}
                      <div className="h-[400px] lg:h-full min-h-[400px] rounded-lg overflow-hidden border border-gray-300">
                        <MapContainer
                          center={SANTO_DOMINGO_CENTER}
                          zoom={15}
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <FeatureGroup>
                            <EditControl
                              position="topright"
                              onCreated={handleLineCreated}
                              onEdited={handleLineEdited}
                              onDeleted={handleLineDeleted}
                              draw={{
                                rectangle: false,
                                circle: false,
                                circlemarker: false,
                                marker: false,
                                polygon: false,
                                polyline: {
                                  shapeOptions: {
                                    color: '#3b82f6',
                                    weight: 4
                                  }
                                }
                              }}
                            />
                            {/* Mostrar línea existente si hay */}
                            {editForm.linea && editForm.linea.length > 0 && (
                              <Polyline
                                positions={editForm.linea}
                                pathOptions={{
                                  color: '#3b82f6',
                                  weight: 4
                                }}
                              />
                            )}
                          </FeatureGroup>
                        </MapContainer>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex gap-3 flex-shrink-0">
                    <button
                      onClick={() => setIsConfigModalOpen(false)}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveConfig}
                      className="flex-1 px-4 py-2.5 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex justify-center items-center gap-2"
                    >
                      <FaSave /> Guardar Configuración
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default Zonificacion;
